import React from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  useBreakpointValue,
  Divider,
} from "@chakra-ui/react";
import { getStaticCaseStudyData, isStaticCaseStudy as checkIsStaticCaseStudy } from "../../utils/caseStudyData";

// Component to render multi-part description with proper spacing
const MultiPartDescription = ({ title, fontSize }) => {
  const caseData = getStaticCaseStudyData(title);
  
  if (!caseData) {
    console.log('No matching case study data found for title:', title);
    return null;
  }

  // Get all parts dynamically
  const parts = [];
  let partIndex = 1;
  while (caseData[`part${partIndex}`]) {
    parts.push(caseData[`part${partIndex}`]);
    partIndex++;
  }

  return (
    <VStack spacing={2} w="full" align="stretch">
      {parts.map((partText, index) => (
        <Text
          key={index}
          fontSize={fontSize}
          color="gray.700"
          lineHeight="1.2"
          textAlign="justify"
          mb={index === parts.length - 1 ? 0 : 2}
        >
          {partText}
        </Text>
      ))}
    </VStack>
  );
};

const ProjectInfoSection = ({
  caseStudy = {},
  containerMaxWidth = "7xl",
}) => {
  // Responsive configurations - must be called before any conditionals
  const headingFontSize = useBreakpointValue({
    base: "2xl",
    md: "3xl",
    lg: "4xl",
  });

  const textFontSize = useBreakpointValue({
    base: "sm",
    md: "md",
    lg: "lg",
  });

  // Use caseStudy data with fallbacks
  const projectData = {
    title: caseStudy.title || caseStudy.imageHeader?.title,
    description: caseStudy.projectInformation || 
                 caseStudy.description || 
                 caseStudy.projectInfo?.description ||
                 caseStudy.imageHeader?.description || "",
    url: caseStudy.url || "",
    developer: caseStudy.clientName || caseStudy.projectInfo?.title || "",
    objectives: caseStudy.objectives || caseStudy.Objectives || null,
  };

  // Check if this is one of our static case studies
  const isStaticCaseStudy = checkIsStaticCaseStudy(projectData.title);

  // Check if component should render at all
  const hasTitle = projectData.title && projectData.title.trim().length > 0;
  const hasDescription = projectData.description && projectData.description.trim().length > 0;
  const hasObjectives = projectData.objectives?.objectiveItems?.length > 0;
  const hasAnyData = hasTitle || hasDescription || hasObjectives;

  // If no meaningful data, don't render the component
  if (!hasAnyData) {
    return null;
  }

  // Dynamic section spacing based on overall content
  const getDynamicSectionSpacing = () => {
    const hasDescription = projectData.description.length > 0;
    const hasObjectives = projectData.objectives?.objectiveItems?.length > 0;
    const objectiveCount = projectData.objectives?.objectiveItems?.length || 0;
    
    if (!hasDescription && !hasObjectives) return { base: 4, md: 6 };
    if (hasDescription && hasObjectives) {
      // More spacing if many objectives
      return objectiveCount > 4 ? { base: 3, md: 5 } : { base: 4, md: 6 };
    }
    return { base: 3, md: 5 }; // One section has content
  };

  // Dynamic header title spacing
  const getHeaderTitleSpacing = () => {
    const hasDescription = projectData.description.length > 0;
    return hasDescription ? { base: 3, md: 4 } : { base: 2, md: 3 };
  };


  return (
    <Box
      py={{ base: 4, md: 6, lg: 8 }}
      sx={{
        backgroundImage: "url('/png/background.png')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <Container maxW={containerMaxWidth}>
        <VStack spacing={isStaticCaseStudy ? { base: 6, md: 8 } : getDynamicSectionSpacing()} align="stretch">
          {/* Project Information Header */}
          <Box>
            <Heading
              fontSize={headingFontSize}
              fontWeight="bold"
              color="#1E3A8A"
              mb={getHeaderTitleSpacing()}
              lineHeight="shorter"
            >
              {projectData.title}
            </Heading>

            {/* Project Description - Static or Dynamic */}
            {isStaticCaseStudy ? (
              <MultiPartDescription 
                title={projectData.title}
                fontSize={textFontSize}
              />
            ) : projectData.description && (
              <Text
                fontSize={textFontSize}
                color="gray.700"
                lineHeight="tall"
                textAlign="justify"
                mb={0}
              >
                {projectData.description}
              </Text>
            )}
          </Box>

          {/* Divider */}
          <Divider borderColor="gray.200" />

        </VStack>
      </Container>
    </Box>
  );
};

export default ProjectInfoSection;
